import { Elements } from '@kontent-ai/delivery-sdk';
import React, { useRef, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import DOMPurify from 'dompurify';
import { useModalPositioning } from '../Utilities/ModalPositioning';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../CSS/Modal.css';

// Utility function to check if rich text contains meaningful content
const hasMeaningfulText = (richText: any): boolean => {
  if (!richText?.value) return false; // Check if value exists
  const strippedText = DOMPurify.sanitize(richText.value, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  }).trim(); // Remove all HTML tags and trim whitespace
  return /^[\w\s]+$/.test(strippedText); // Return true if content has alphanumeric or whitespace characters only
};

interface CustomModalFramelessProps {
  show: boolean;
  onHide: () => void;
  element: {
    headerTitle?: { value?: string };
    footerTitle?: { value?: string };
    header_image?: { value?: { description?: string }[] };
    footer_image?: { value?: { description?: string }[] };
    image_permission_language?: { value?: string };
    digital_asset_id?: { value?: string };
  };
  selectedImage: {
    url: string;
    description?: string;
    title?: string;
    digitalAssetID?: string;
    permissionLang: Elements.RichTextElement;
  };
  headerTitle?: { value?: string };
  footerTitle?: { value?: string };
  position?: { top: number; left: number } | null;
}

const CustomModalFrameless: React.FC<CustomModalFramelessProps> = ({
  show,
  onHide,
  element: _element,
  selectedImage,
  position,
}) => {
  const isInIframe = window.parent && window.parent !== window;

  const modalRef = useRef<HTMLDivElement>(null);
  useModalPositioning(position ?? null, show);

  useEffect(() => {
    if (isInIframe) {
      // Add iframe class to body
      document.body.classList.add('in-iframe');

      // Manage modal-open class for scrolling
      if (show) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }

      return () => {
        document.body.classList.remove('modal-open');
        document.body.classList.remove('in-iframe');
      };
    }
  }, [show, isInIframe]);

  const modalKey = selectedImage.url || 'default';

  return (
    <div ref={modalRef}>
      <Modal
        key={modalKey}
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="modal-N100DF"
        role="dialog"
        tabIndex={-1}
        backdrop={true} // Allows clicking outside to close the modal
        keyboard={true} // Allows closing with the Escape key
        className="modal-fade in"
        style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      >
        <Modal.Header className="modal-header">
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              color: '#000',
              opacity: 0.5,
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
          {selectedImage?.title && (
            <Modal.Title id="modal-N100DF" className="modal-title">
              {selectedImage.title || ''}
            </Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body>
          <img
            src={selectedImage?.url}
            alt={selectedImage?.description}
            className="img-responsive center-block"
          />
        </Modal.Body>
        {(selectedImage?.digitalAssetID || selectedImage?.permissionLang) && (
          <Modal.Footer className="modal-footer">
            <div
              className="da-footer"
              style={{ marginTop: '0', paddingLeft: '0px' }}
            >
              <span className="daid" style={{ textAlign: 'left' }}>
                {hasMeaningfulText(selectedImage.permissionLang) &&
                  DOMPurify.sanitize(selectedImage.permissionLang?.value, {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: [],
                  }).trim()}
                {selectedImage.digitalAssetID &&
                  ` [${selectedImage.digitalAssetID}]`}
              </span>
            </div>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default CustomModalFrameless;
